import { Clock01Icon, Delete02Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useScheduledReports,
  useCreateScheduledReport,
  useDeleteScheduledReport,
  useUpdateScheduledReport,
} from '@/hooks/use-reports';

export const Route = createFileRoute('/_authenticated/reports/scheduled')({
  component: ScheduledReportsPage,
});

import { DASHBOARD_OPTIONS } from '@/lib/dashboards';

function ScheduledReportsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useScheduledReports();
  const createMutation = useCreateScheduledReport();
  const deleteMutation = useDeleteScheduledReport();
  const updateMutation = useUpdateScheduledReport();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    dashboardId: 'production-overview',
    format: 'pdf' as 'pdf' | 'xlsx' | 'csv',
    frequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    time: '09:00',
    recipients: '',
  });

  const scheduledReports = data?.data ?? [];

  const handleCreate = () => {
    if (!form.name || !form.recipients) {
      toast.error('Name and recipients are required');
      return;
    }

    const recipients = form.recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      toast.error('At least one valid email is required');
      return;
    }

    createMutation.mutate(
      {
        name: form.name,
        description: form.description,
        dashboardId: form.dashboardId,
        format: form.format,
        frequency: form.frequency,
        time: form.time,
        recipients,
      },
      {
        onSuccess: () => {
          toast.success(t('reports.scheduled.created', 'Scheduled report created'));
          setIsCreateOpen(false);
          setForm({
            name: '',
            description: '',
            dashboardId: 'production-overview',
            format: 'pdf',
            frequency: 'weekly',
            time: '09:00',
            recipients: '',
          });
        },
        onError: (err: Error) => toast.error(err.message),
      },
    );
  };

  const handleToggle = useCallback(
    (id: string, enabled: boolean) => {
      updateMutation.mutate(
        { id, enabled },
        {
          onSuccess: () => toast.success(enabled ? 'Report enabled' : 'Report disabled'),
        },
      );
    },
    [updateMutation],
  );

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => toast.success(t('reports.scheduled.deleted', 'Scheduled report deleted')),
    });
  };

  const getFrequencyLabel = (freq: string) => {
    switch (freq) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      default:
        return freq;
    }
  };

  const getFormatBadge = (format: string) => {
    const colors: Record<string, string> = {
      pdf: 'bg-red-100 text-red-700',
      xlsx: 'bg-green-100 text-green-700',
      csv: 'bg-blue-100 text-blue-700',
    };
    return colors[format] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HugeiconsIcon icon={Clock01Icon} className="size-6" />
            {t('sidebar.reports.scheduled', 'Scheduled Reports')}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t(
              'reports.scheduled.description',
              'Automated report delivery via email on a recurring schedule',
            )}
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <HugeiconsIcon icon={PlusSignIcon} className="size-4 mr-2" />
              {t('reports.scheduled.create', 'Create Schedule')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {t('reports.scheduled.createTitle', 'New Scheduled Report')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'reports.scheduled.createDescription',
                  'Set up automated report delivery to your team',
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Report Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Weekly Maintenance Summary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Dashboard</Label>
                  <Select
                    value={form.dashboardId}
                    onValueChange={(v) => setForm((f) => ({ ...f, dashboardId: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DASHBOARD_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Format</Label>
                  <Select
                    value={form.format}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, format: v as 'pdf' | 'xlsx' | 'csv' }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Frequency</Label>
                  <Select
                    value={form.frequency}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, frequency: v as 'daily' | 'weekly' | 'monthly' }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="recipients">Recipients (comma-separated emails)</Label>
                <Input
                  id="recipients"
                  value={form.recipients}
                  onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                  placeholder="manager@company.com, admin@company.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading…</div>
      ) : scheduledReports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HugeiconsIcon
              icon={Clock01Icon}
              className="size-12 mx-auto text-muted-foreground/30 mb-4"
            />
            <p className="text-muted-foreground">No scheduled reports yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first scheduled report to automate report delivery.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {scheduledReports.map((report) => (
            <Card key={report.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{report.name}</CardTitle>
                    {report.description && (
                      <CardDescription className="text-xs mt-1">
                        {report.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={report.enabled}
                      onCheckedChange={(checked) => handleToggle(report.id, checked)}
                      disabled={updateMutation.isPending}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(report.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <HugeiconsIcon icon={Delete02Icon} className="size-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">{getFrequencyLabel(report.frequency)}</Badge>
                  <Badge className={getFormatBadge(report.format)}>
                    {report.format.toUpperCase()}
                  </Badge>
                  <span className="text-muted-foreground">at {report.time}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">
                    {Array.isArray(report.recipients) ? report.recipients.length : 0} recipient(s)
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
